import { jobLists } from "character";
import FormComponents from "components/forms/FormComponents";
import JobIcon from "components/job-icon/JobIcon";
import NoCharacter from "components/no-character/NoCharacter";
import RelicCheckbox from "components/relic-checkbox/RelicCheckbox";
import { items } from "data-files/items";
import { GameData, InputType, Job, RelicData } from "enums";
import manager from "managers/app";
import { ViewRelicInfo } from "managers/types/relic-types";
import { Component } from "react";
import { connect } from "react-redux";
import t from "translations/translator";
import { AppState } from "types/app";
import { Character } from "types/data";
import ContentHeader from "views/page-parts/content/content-header/ContentHeader";
import DetailsSection from "views/page-parts/content/details-section/DetailsSection";
import Obtained from "views/page-parts/content/obtained/Obtained";
import OverviewTable from "views/page-parts/content/overview-table/OverviewTable";
import { Requirement } from "views/page-parts/content/relic-requirements/types";
import { ViewToolResplendentResplendentProps } from "views/relics/tools/resplendent/resplendent/types";
import { TaskInfo } from "views/type";
import "./ToolResplendentResplendent.css";

const mapStateToProps = (state:AppState):ViewToolResplendentResplendentProps.Redux => {
	return {
		lang: state.userInfo.options.lang,
		gameLang: state.userInfo.options.gameLang,
		userInfo: state.userInfo,
		hideCompletedSteps: state.userInfo.options.hideCompletedSteps,
		hideCompletedTasks: state.userInfo.options.hideCompletedTasks,
		showMaximumItemQty: state.userInfo.options.showMaximumItemQty,
		showJob: state.userInfo.options.job,
	};
};

class ToolResplendentResplendent extends Component<ViewToolResplendentResplendentProps.Component> {
	private requirements:Requirement[] = [];
	private importantNPCs:GameData.NPC[] = [];
	private relicInfo:ViewRelicInfo = {
		jobs: jobLists.relic[RelicData.Group.TOOL_RESPLENDENT],
		type: RelicData.Type.TOOL,
		relic: RelicData.Group.TOOL_RESPLENDENT,
		part: RelicData.Part.TOOL_RESPLENDENT,
		steps: [
			RelicData.Step.RESPLENDENT,
		],
		stepPlaceholders: {},
		next: null,
		previous: null,
	};

	constructor(props:ViewToolResplendentResplendentProps.Component){
		super(props);
		this.requirements = this.getRequirements();
		this.importantNPCs = this.getImportantNPCs();
	}

	render():JSX.Element{
		const character = manager.data.getActiveCharacter();
		if(character === null){ return <NoCharacter />; }

		const completeSteps = manager.content.getCompleteSteps(character.progress, this.relicInfo);

		const step = this.relicInfo.steps[0];
		let row = "odd-row";

		return (
			<div id="tool-cosmic-stellar" className="relic">
				<ContentHeader requirements={this.requirements} importantNPCs={this.importantNPCs} relicInfo={this.relicInfo}>

				</ContentHeader>
				<div id="relic-checklist">
					<OverviewTable character={character} completeSteps={completeSteps} relicInfo={this.relicInfo} />
				</div>
				<div id="relic-details">
					<h1>{t.t("Details")}</h1>
					{this.props.hideCompletedSteps && completeSteps.allComplete ? t.t("All Steps Complete") : null }
					<DetailsSection title={step} id={step} placeholders={this.relicInfo.stepPlaceholders[step]} stepComplete={completeSteps[step]} key={step}>
						<p>{t.t("It is recommended to work on 1 relic at a time, as you get a global research bonus when you have a number of %PART% tools", { PART: this.relicInfo.part})}</p>
						<div className="table-wrapper">
							<table className="table">
								<thead><tr>
									<th>{t.t("Job")}</th>
									<th>{t.t("Task")}</th>
									<th>{t.t("Found")}</th>
									<th>{t.t("Quantity")}</th>
									<th>{t.t("Inventory")}</th>
									<th>{t.t("To Obtain")}</th>
									<th>{t.t("Done")}</th>
								</tr></thead>
								<tbody>
									{
										this.relicInfo.jobs.map((job) => {
											if(this.props.showJob !== null && this.props.showJob !== job){ return null; }
											const stepComplete = character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, job);

											if(this.props.hideCompletedSteps && stepComplete){ return null; }

											const taskInfo = this.getTasksInfo(step, character, job);

											const tableRows = taskInfo.map((thisTask, taskIndex) => {
												if(this.props.hideCompletedTasks && thisTask.complete){ return null; }

												const targetItem = thisTask.target as GameData.Item;
												const inventory = manager.data.getInventoryStatus({
													item: targetItem,
													relic: [this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, thisTask.task],
													job: job,
													getMax: this.props.showMaximumItemQty,
												});

												const showJobIcon = manager.content.shouldShowJobIcon(this.props.hideCompletedTasks, taskInfo, taskIndex);
												const rowSpan = manager.content.getRowSpan(this.props.hideCompletedTasks, taskInfo, taskIndex);

												return <tr key={`${job}-${step}-${thisTask.task}`} className={row}>
													{showJobIcon ? <td rowSpan={rowSpan}><JobIcon job={job} /></td> : null}
													<td>{t.t(targetItem)}</td>
													<td><Obtained obtained={items[targetItem].obtained} qtyNeeded={inventory.total} /></td>
													<td>{inventory.qtyPerJob.toLocaleString()}</td>
													<td>
														<FormComponents.TextInput
															label={{ hide: true }}
															input={{ type: InputType.NUMBER, value: inventory.inventory, dataAttributes: { item: targetItem }, events: { onChange: manager.content.inventoryUpdated }}}
														/>
													</td>
													<td>{inventory.toObtain.toLocaleString()}</td>
													<td><RelicCheckbox character={character} job={job} relicInfo={this.relicInfo} step={step} task={thisTask.task} /></td>
												</tr>;
											});
											row = row === "odd-row" ? "even-row" : "odd-row";
											return tableRows;
										})
									}
									{
										completeSteps[step]
											? <tr className="all-complete"><td colSpan={7}><p>{t.t("All Complete")}</p></td></tr>
											: null
									}
								</tbody>
							</table>
						</div>
					</DetailsSection>
				</div>
			</div>
		);
	}

	componentDidMount():void{
		manager.view.changeComplete();
	}

	private getRequirements():Requirement[]{
		if(!this.relicInfo || this.relicInfo.previous === null){ return []; }

		return [
			{ type: "previous part", part: this.relicInfo.previous.part, partView: this.relicInfo.previous.view },
		];
	}

	private getImportantNPCs():GameData.NPC[]{
		return [GameData.NPC.RESEARCHINGWAY];
	}

	private getTasksInfo(step:RelicData.Step, character:Character, job:Job):TaskInfo[]{
		const tasksInfo:TaskInfo[] = [];
		const tasks = [RelicData.Task.TASK_1];

		if(jobLists.type.crafter.includes(job)){
			tasks.push(...[RelicData.Task.TASK_2, RelicData.Task.TASK_3, RelicData.Task.TASK_4]);
		}

		tasks.forEach((task) => {
			const target = manager.relics.getTaskItem(step, task, job);
			const complete = character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, task, job);
			tasksInfo.push({ task, complete, target });
		});
		return tasksInfo;
	}
}
export default connect(mapStateToProps)(ToolResplendentResplendent);
